<template>
  <div>
    <div container>
      <v-alert v-if="isInactive" :value="true" class="_mbxs _mtxs" type="error">
        This administrator is currently inactive.
      </v-alert>
    </div>
    <app-page-title class="_mts _mbs">
      <template #pageTitleLeft>
        <h1>{{ fullName }}</h1>
      </template>
      <template #pageTitleRight>
        <div v-if="confirmingDeactivate">
          WARNING: This action cannot be undone. Please confirm you want to
          permanently delete this user account.
          <p class="lips" v-if="isPrimaryContact">
            This user is a primary contact.
          </p>
          <button
            class="_mls btn --sm"
            :disabled="deactivating"
            @click="confirmingDeactivate = false"
          >
            No
          </button>
          <async-button
            class="_mls btn --sm white lips"
            :executing="deactivating"
            @clicked="deactivate"
          >
            Yes
          </async-button>
        </div>
        <div v-else>
          <router-link
            v-if="!isInactive && !isSelf"
            class="_mls btn --sm"
            :to="{
              name: 'update-user-password',
              params: { userId: hiddenRouteParams.userId }
            }"
          >
            Password Reset
          </router-link>

          <router-link
            v-if="isSelf && !$isSsoUser"
            class="_mls btn white --sm"
            :to="{ name: 'update-your-password' }"
          >
            Change My Password
          </router-link>
          <dropdown-link
            v-if="!isInactive && !isSelf && !disableClone"
            title="Clone"
            class="_mls btn --sm"
          >
            <li class="_mbxs _mtxs _text-right">
              <a @click="cloneToNewUser">To New User</a>
            </li>
            <li v-if="$isInternalUser" class="_mbxs _text-right">
              <a @click="showConfirmCloneTo = true">To Existing User</a>
            </li>
          </dropdown-link>
          <button
            v-if="!isInactive && !isSelf"
            class="_mls btn white lips --sm"
            @click="confirmingDeactivate = true"
          >
            Deactivate
          </button>
          <button class="_mls btn --sm" v-if="!$isSsoUser" @click="onEditClick">
            Edit Administrator
          </button>
        </div>
      </template>
    </app-page-title>

    <div v-if="showConfirmCloneTo" container>
      <div class="announcements _mbxs">
        <div class="announcement -notice">
          <p>Clone this user's groups to which user:</p>
          <div class="anncoucement-button _flex">
            <app-placeholder-input
              class="_c-text-primary _b1"
              v-model="cloneToId"
              valueKey="cloneToId"
              label="User ID to Clone To"
            />
            <button class="_mlxs btn --sm" @click="showConfirmCloneTo = false">
              Cancel
            </button>
            <button class="_mlxs btn --sm white" @click="continueClone">
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>

    <div container>
      <h2>Administrator Details</h2>
      <div grid class="_mbxs">
        <div v-if="isInactive" column>
          <h4>Status:</h4>
          <p>Inactive</p>
        </div>
        <div column>
          <h4>User ID:</h4>
          <p>{{ user.userId }}</p>
        </div>
        <div column>
          <h4>Email:</h4>
          <p>{{ user.email }}</p>
        </div>
        <div column>
          <h4>Account Type:</h4>
          <p>{{ accountTypeByCode(user.accountType) }}</p>
        </div>
        <div column>
          <h4>Invoice Emails?</h4>
          <p>{{ user.emailInvoice ? 'Yes' : 'No' }}</p>
        </div>
        <div column>
          <h4>SMS Opt In?</h4>
          <p>{{ user.smsEnabled ? 'Yes' : 'No' }}</p>
        </div>
      </div>
      <div v-show="user.smsEnabled" class="fade-row bg-light-gray-2 _pxs _mbxs">
        <div v-html="cloudCannonContent.sms_disclaimer"></div>
      </div>
    </div>
    <pending-attestations
      v-if="!isInactive && showPending"
      :readonly="true"
      v-model="user.role"
      title="Pending Permissions"
    />
    <app-user-acls v-if="!isInactive" :readonly="true" :role="user.role" />

    <div v-if="!attemptedLoad">
      <app-section-header class="advanced-table-section-header _bb1">
        <template #text>
          <p>Transaction History</p>
        </template>
      </app-section-header>
      <div container class="_pts _pbs _text-center">
        <button type="button" class="btn --sm" @click="loadHistory">
          Show Transaction History
        </button>
      </div>
    </div>

    <app-advanced-table-section
      v-else
      class="_bt1"
      :custom-sort="tableSorter"
      :descending="true"
      :loading="historyLoading"
      title="Transaction History"
      :headers="transactionHistoryHeaders"
      id="transation-history"
      :items="transactionHistoryItems"
    >
    </app-advanced-table-section>
  </div>
</template>

<script>
import { computed, onBeforeMount, ref, watch } from '@vue/composition-api';
import { useStore, useRoute, useRouter } from '@vue3shims';
import AppAdvancedTableSection from '@sections/AdvancedTableSection.vue';
import AppSectionHeader from '@organisms/SectionHeader.vue';
import AppPageTitle from '@organisms/PageTitle';
import AppUserAcls from '@organisms/v2/UserACLs';
import PendingAttestations from '@organisms/PendingAttestations.vue';
import AppPlaceholderInput from '@atoms/PlaceholderInput';
import AsyncButton from '@molecules/AsyncButton';
import DropdownLink from '@atoms/DropdownLink';
import RequestHelper from '@mixins/RequestHelper';
import tableSorter from '@mixins/tableSorter';

import UserManagementHelper from '@mixins/UserManagementHelper';
import { useHiddenRouteParams } from '@composables/useHiddenRouteParams';

export default {
  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const { hiddenRouteParams } = useHiddenRouteParams();
    const cloudCannonContent = computed(
      () => store.getters['cloudCannon/cloudCannonContent']
    );
    const cloudCannonContentLoading = computed(
      () => store.getters['cloudCannon/cloudCannonContentLoading']
    );
    const user = computed(() => store.getters['users2/activeUser']);
    const userId = computed(() => hiddenRouteParams.value.userId);
    const userRole = computed(
      () => store.getters['users2/activeUser'].role || {}
    );
    const attemptedLoad = ref(false);
    const disableClone = computed(
      () =>
        userRole.value.subGroupAccess &&
        userRole.value.subGroupAccess.length < 1
    );
    const cloneToId = ref('');
    const confirmingDeactivate = ref(false);
    const deactivating = computed(() => store.getters['users2/deactivating']);
    const errorDeactivating = false;
    const errorDeactivatingUserMessage = ref('');
    const fullName = computed(
      () => `${user.value.firstName} ${user.value.lastName}`
    );
    const history = computed(
      () => store.getters['users2/transactionHistory'] || []
    );
    const historyLoading = computed(
      () => store.getters['users2/historyLoading']
    );
    const isInactive = computed(() => user.value.status === 'Inactive');
    const isPrimaryContact = computed(
      () =>
        userRole.value &&
        userRole.value.minimumAccess &&
        userRole.value.minimumAccess.primaryContact
    );
    const isSelf = computed(() => userId.value === store.getters['userId']);
    const pendingAttestations = computed(
      () => store.getters['users2/pendingAttestations']
    );
    const showConfirmCloneTo = ref(false);
    const showPending = computed(
      () =>
        userRole.value.subGroupAccessPending &&
        userRole.value.subGroupAccessPending.length > 0
    );
    const transactionHistoryHeaders = [
      { text: 'Date', align: 'left', value: 'date' },
      { text: 'Group ID', value: 'groupId' },
      { text: 'Subgroup', value: 'subGroup' },
      { text: 'Module', value: 'module' },
      { text: 'Action', value: 'action' },
      { text: 'User Name', value: 'modifiedUser' }
    ];
    const transactionHistoryItems = computed(() => {
      return history.value.map(h => ({
        ...h,
        date: h.modifiedDate,
        groupId: h.groupId,
        subGroup:
          h.subGroupName && h.subGroupId
            ? `${h.subGroupName} - ${h.subGroupId}`
            : '',
        module: h.access,
        action: h.accessAction,
        userName: h.clientId
      }));
    });

    const cloneToNewUser = () => {
      store.commit('users2/CLEAR_ACTIVE_USER');
      router.push({
        name: 'user-add',
        params: { cloneFrom: hiddenRouteParams.value.userId }
      });
    };

    const continueClone = () => {
      store.commit('users2/CLEAR_ACTIVE_USER');
      router.push({
        name: 'user-edit',
        params: {
          userId: cloneToId.value,
          cloneFrom: userId.value
        },
        query: {
          cloningToExistingUser: true
        }
      });
    };

    const deactivate = async () => {
      const result = await store.dispatch(
        'users2/deactivateUser',
        userId.value
      );
      if (result === true) {
        store.commit('SET_ANNOUNCEMENT', 'User has been deactivated');
        confirmingDeactivate.value = false;
        loadUser();
      }
    };

    const loadUser = async () => {
      const payload = { userId: userId.value, edit: false };
      // console.log('loadUser:payload', payload);

      return await store.dispatch('users2/fetchUserWithRoleById', payload);
    };

    const loadHistory = () => {
      attemptedLoad.value = true;
      store.dispatch('users2/fetchTransactionHistory', userId.value);
    };

    const onEditClick = () => {
      store.commit('users2/SET_ACTIVE_USER', user.value);
      router.push({
        name: 'user-edit',
        params: {
          userId: userId.value
        }
      });
    };

    onBeforeMount(async () => {
      // console.log('route params', hiddenRouteParams.value);

      await loadUser();
      // console.log('post-load:', user.value);

      await store.dispatch('cloudCannon/GET_CLOUD_CANNON_CONTENT', {
        cloudCannonResource: '/my_profile.json'
      });
    });

    watch(userId, () => {
      loadUser();
    });

    return {
      attemptedLoad,
      cloneToId,
      cloneToNewUser,
      confirmingDeactivate,
      deactivate,
      deactivating,
      disableClone,
      fullName,
      history,
      historyLoading,
      isInactive,
      isPrimaryContact,
      isSelf,
      pendingAttestations,
      showConfirmCloneTo,
      showPending,
      transactionHistoryHeaders,
      transactionHistoryItems,
      user,
      hiddenRouteParams,
      userId,
      userRole,
      cloudCannonContent,
      cloudCannonContentLoading,
      continueClone,
      loadHistory,
      loadUser,
      onEditClick
    };
  },
  mixins: [RequestHelper, UserManagementHelper, tableSorter],
  components: {
    AppAdvancedTableSection,
    AppPageTitle,
    AppPlaceholderInput,
    AppSectionHeader,
    AppUserAcls,
    AsyncButton,
    DropdownLink,
    PendingAttestations
  }
};
</script>
